import * as React from "react";

import { useEffect, useMemo } from "react";
import { MathfieldElement } from "mathlive";
import { create } from "jss";
import camelCase from "jss-plugin-camel-case";

if (typeof window !== "undefined") {
  const OriginalAudioContext = window.AudioContext || window.webkitAudioContext;
  if (OriginalAudioContext) {
    window.AudioContext = class extends OriginalAudioContext {
      decodeAudioData() {
        // Return a silent empty buffer instead of rejecting
        // This prevents MathLive from logging any error
        const sampleRate = this.sampleRate || 44100;
        const buffer = this.createBuffer(1, 1, sampleRate);
        return Promise.resolve(buffer);
      }
    };
  }
}

window.mathVirtualKeyboard = window.mathVirtualKeyboard || {};
MathfieldElement.soundsDirectory = null;

// Add this function back and uncomment it
const addKeyboardStyle = (mfe) => {
  const style = document.createElement("style");
  style.textContent = `
    math-field::part(virtual-keyboard-toggle) {
      fill: #000000 !important;
      opacity: 1 !important;
      visibility: visible !important;
      display: inline-block !important;
    }

    math-field::part(virtual-keyboard-toggle):after {
      top: 0 !important;
    }
    
    math-field:not(:focus-within)::part(virtual-keyboard-toggle) {
      opacity: 0.6 !important;
      visibility: visible !important;
    }
    
    math-field:hover::part(virtual-keyboard-toggle),
    math-field:focus-within::part(virtual-keyboard-toggle) {
      opacity: 1 !important;
    }

    /* ── Fix white text in autocomplete popup ── */
    .ML__autocomplete {
      color: #000000 !important;
      background: #ffffff !important;
    }

    .ML__autocomplete ul li {
      color: #000000 !important;
    }

    .ML__autocomplete ul li:hover,
    .ML__autocomplete ul li[aria-selected="true"] {
      background: #e8e8e8 !important;
      color: #000000 !important;
    }

    /* Fix the main field text color */
    .ML__content {
      color: #000000 !important;
    }

    .ML__latex {
      color: #000000 !important;
    }
    :host {
      display: block;
      min-height: 60px;
      box-sizing: border-box;
    }

    .ML__content {
      color: #000000 !important;
      white-space: pre-wrap !important; 
      word-break: break-word !important;
    }
    .ML__latex {
      color: #000000 !important;
    }
  `;
  mfe.shadowRoot.appendChild(style);
};
export const MathEditor = (props) => {
  const containerRef = React.useRef(null);
  const mfe = useMemo(
    () => new MathfieldElement({
      virtualKeyboardMode: "manual",
      ...props.options,
      sounds: false,
      keypressSound: null,
      spacebarKeypressSound: null,
      returnKeypressSound: null,
      deleteKeypressSound: null,
      inlineShortcutsTimeout: 0,
      inlineShortcuts: {},
      mathModeSpace: "\\,",
      popoverPolicy: "off",
    }),
    [props.options]
  );
  useEffect(() => {
    const container = containerRef.current;
    container.innerHTML = "";
    container.appendChild(mfe);

    mfe.className = props.className || "";

    // ── Track last good value ──
    let lastGoodValue = "";

    mfe.addEventListener("input", ({ target }) => {
      lastGoodValue = target.value || "";
      props.onChange(lastGoodValue);
    });

    // ── Intercept backspace at the document level BEFORE mathlive gets it ──
    const handleKeyDown = (e) => {
      if (e.key !== "Backspace") return;
      if (!mfe.contains(document.activeElement) && document.activeElement !== mfe) return;

      const valueBefore = mfe.value;

      // Let MathLive process it first, then check if value actually changed
      setTimeout(() => {
        const valueAfter = mfe.value;
        if (valueAfter === valueBefore && valueBefore !== "") {
          // MathLive swallowed the backspace (was in command mode)
          // Force delete the last character from the LaTeX string
          const newValue = valueBefore.replace(/\\?.$/, "");
          mfe.setValue(newValue, { selectionIsCollapsed: true });
          props.onChange(mfe.value);
        }
      }, 0);
    };

    // Use capture phase so we get it before MathLive
    document.addEventListener("keydown", handleKeyDown, true);

    mfe.addEventListener("placeholder-change", ({ detail }) => {
      const { placeholderId } = detail;
      const value = mfe.getPlaceholderField(placeholderId)?.getValue() || "";
      if (props.onPlaceholderChange) {
        props.onPlaceholderChange(placeholderId, value);
      }
    });

    addKeyboardStyle(mfe);
    Object.values(mfe.placeholders).forEach((placeholder) =>
      addStyleEl(placeholder, props.placeholderStyle ?? {})
    );

    // ── Cleanup ──
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);

  useEffect(() => {
    mfe.value = props.value;
  }, [props.value, mfe]);

  return <div ref={containerRef} style={props.containerStyle} />;
};

const addStyleEl = (el, css) => {
  const node = document.createElement("style");
  node.innerHTML = stylesheet("placeholder-mathfield", css);
  el.appendChild(node);
  el.classList.add("placeholder-mathfield");
};

const jss = create({ plugins: [camelCase()] });
const stylesheet = (className, styles) =>
  jss
    .createStyleSheet({ [className]: styles }, { generateId: ({ key }) => key })
    .toString();
